import type { UserDevice } from '@troia-v3/types';
import React from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { getOrCreateDeviceId } from '@/lib/deviceId';
import { DevicesTable, type DeviceRow } from '@/shared/components/devices/DevicesTable';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { FormSection } from '@/shared/components/ui/FormSection';

import { useDevices, useDeleteDevice } from '../hooks/useDevices';

const deviceTitle = (device: UserDevice): string => {
  const base =
    device.browser ?? (device.platform === 'web' ? 'Navegador' : device.platform === 'android' ? 'Android' : 'iOS');
  return device.deviceModel ? `${base} · ${device.deviceModel}` : base;
};

/**
 * Devices Section
 * Lists devices that have logged in to the current user's account (web/android/ios)
 * and allows revoking one — used for push notification dedupe / 2FA device lifecycle.
 * Does NOT publish to the account save button (no batched form state here — deletion is immediate).
 */
export const DevicesSection: React.FC = () => {
  const { data: devices, isLoading, isError } = useDevices();
  const deleteDevice = useDeleteDevice();
  const confirm = useConfirm();
  const { logout } = useAuth();
  const currentDeviceId = getOrCreateDeviceId();

  const handleDelete = async (device: UserDevice): Promise<void> => {
    const isCurrent = device.deviceId === currentDeviceId;
    const confirmed = await confirm({
      title: 'Confirmar exclusão',
      message: isCurrent
        ? 'Este é o dispositivo em uso — você será DESCONECTADO imediatamente após a exclusão. Excluir mesmo assim?'
        : `Excluir o dispositivo "${deviceTitle(device)}"? Ele será desconectado e para de receber notificações push até um novo login.`,
      confirmButtonText: 'Excluir',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await deleteDevice.mutateAsync(device.deviceId);
      // Device removido = acesso revogado: o aparelho atual desloga na hora.
      // Outros aparelhos online caem via socket 'device:removed' (listener global).
      if (isCurrent) {
        logout();
      }
    } catch {
      // Toast global do api client já reporta o erro.
    }
  };

  // `browserLabel` preserva a string que o cliente mandou no login ('Chrome');
  // o restante (tipo de aparelho, SO, IP, localização) vem do parse do
  // User-Agent + Client Hints feito no servidor.
  const rows: DeviceRow[] = (devices ?? []).map(({ browser, ...device }) => ({
    ...device,
    // Devices registrados antes do enriquecimento (2026-08-11) não têm
    // `deviceKind` — mantém a heurística antiga por `platform` para o ícone.
    deviceKind: device.deviceKind ?? (device.platform === 'web' ? 'desktop' : 'smartphone'),
    // `browser` do operador é string livre ('Chrome'); a tabela usa
    // `browserLabel` para não conflitar com o enum de `DeviceInfo`.
    browserLabel: browser,
    badges: [
      ...(device.deviceId === currentDeviceId
        ? [{ text: 'Este dispositivo', color: '#6d28d9', bg: '#ede9fe' }]
        : []),
      ...(!device.token ? [{ text: 'Sem token de push', color: '#b45309', bg: '#fef3c7' }] : []),
    ],
  }));

  const rowsById = new Map((devices ?? []).map((device) => [device.deviceId, device]));

  return (
    <FormSection
      icon="monitor"
      title="Dispositivos conectados"
      description="Navegadores e aparelhos que já fizeram login na sua conta e podem receber notificações push."
    >
      <div data-testid="account-devices-section">
        <DevicesTable
          devices={rows}
          isLoading={isLoading}
          isError={isError}
          emptyMessage="Nenhum dispositivo registrado. Dispositivos aparecem aqui após o login em cada navegador ou aparelho."
          lastActiveLabel="Último login"
          onDelete={(row) => {
            const device = rowsById.get(row.deviceId);
            if (device) void handleDelete(device);
          }}
          deleteDisabled={deleteDevice.isPending}
          testId="account-devices"
        />
      </div>
    </FormSection>
  );
};
